import React from "react";

// Import slice - PATH RELATIVI!
import {FaseLegacy} from "../produzione/odpLegacySlice";
import {isTempoNonAttendibile} from "../../common/claire/tempoUtils";

// Auth
import {useAutorizzazioneGruppi} from "../../authentication/useAutorizzazioneGruppi";
import {PermissionTypes} from "../../authentication/permissionsGroups";

// Componenti locali
import OperatoreSelectInline from "../produzione/OperatoreSelectInline";

// Material UI
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import LinearProgress from "@mui/material/LinearProgress";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

// ============================================
// COSTANTI
// ============================================

const getStatoColor = (
    stato?: number
): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (stato) {
        case 0:
            return "default";
        case 1:
            return "info";
        case 2:
            return "primary";
        case 3:
            return "warning";
        case 4:
            return "success";
        default:
            return "default";
    }
};

// ============================================
// PROPS
// ============================================

interface OdPSidebarFasiProps {
    fasi: FaseLegacy[];
    odpId: number;
}

// ============================================
// COMPONENT
// ============================================

const OdPSidebarFasi = ({fasi, odpId}: OdPSidebarFasiProps) => {
    const consentiModificaOperatore = useAutorizzazioneGruppi([
        PermissionTypes.PIANIFICATORI,
    ]);

    if (!fasi || fasi.length === 0) {
        return (
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{display: "block", textAlign: "center", py: 1}}
            >
                Nessuna fase
            </Typography>
        );
    }

    return (
        <Box sx={{px: 0.5}}>
            {fasi.map((fase, index) => (
                <Box key={fase.id}>
                    {index > 0 && <Divider sx={{my: 0.5}}/>}
                    <Box sx={{py: 0.5}}>
                        {/* Riga 1: numero fase + lavorazione + stato */}
                        <Stack
                            direction="row"
                            spacing={0.5}
                            sx={{
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >

                            <Typography
                                variant="caption"
                                fontWeight="bold"
                                sx={{minWidth: 20}}
                            >
                                {fase.numero_fase}
                            </Typography>
                            <Typography
                                variant="caption"
                                noWrap
                                sx={{flexGrow: 1}}
                            >
                                {fase.descrizione || fase.codice_lavorazione || "-"}
                            </Typography>
                            {isTempoNonAttendibile(fase.ore_previste) && (
                                <Tooltip
                                    title="Tempo non attendibile: valore assente o inferiore alla soglia minima in eSolver">
                                    <WarningAmberIcon sx={{fontSize: 14, color: "warning.main"}}/>
                                </Tooltip>
                            )}
                            <Tooltip title={
                                <Stack
                                    direction="column"
                                    spacing={0.5}
                                    sx={{
                                        justifyContent: "center",
                                        alignItems: "flex-start",
                                    }}
                                >
                                    <Divider textAlign="left">QUANTITÀ:</Divider>

                                    <Typography
                                        variant="caption"
                                        noWrap
                                        sx={{pl: 3}}
                                    >{`in ordine:${Intl.NumberFormat("it-IT", {
                                        style: "decimal",
                                        useGrouping: true,
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 0,
                                    }).format(fase.quantita ?? 0)} pz`}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        noWrap
                                        sx={{pl: 3}}
                                    >{`schedulata: ${Intl.NumberFormat("it-IT", {
                                        style: "decimal",
                                        useGrouping: true,
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 0,
                                    }).format(fase.quantita_schedulata ?? 0)}pz`}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        noWrap
                                        sx={{pl: 3}}
                                    >{`lavorata: ${Intl.NumberFormat("it-IT", {
                                        style: "decimal",
                                        useGrouping: true,
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 0,
                                    }).format(fase.quantita_lavorata ?? 0)} pz`}
                                    </Typography>
                                    <Divider textAlign="left">ORE:</Divider>
                                    <Typography
                                        variant="caption"
                                        noWrap
                                        sx={{pl: 3}}
                                    >{`totali previste a sistema: ${Intl.NumberFormat("it-IT", {
                                        style: "decimal",
                                        useGrouping: true,
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 2,
                                    }).format(fase.ore_previste ?? 0)} h`}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        noWrap
                                        sx={{pl: 3}}
                                    >{`residue a sistema: ${Intl.NumberFormat("it-IT", {
                                        style: "decimal",
                                        useGrouping: true,
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 2,
                                    }).format(fase.ore_previste_residue ?? 0)}h`}
                                    </Typography>

                                </Stack>
                            }>
                                <Stack
                                    direction="row"
                                    spacing={0.5}
                                    sx={{
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                    }}
                                >


                                    <Typography
                                        variant="caption"
                                        noWrap
                                        sx={{flexGrow: 1}}
                                    >{`${Intl.NumberFormat("it-IT", {
                                        style: "decimal",
                                        useGrouping: true,
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 0,
                                    }).format(fase.quantita_lavorata ?? 0)}/${Intl.NumberFormat("it-IT", {
                                        style: "decimal",
                                        useGrouping: true,
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 0,
                                    }).format(fase.quantita_schedulata ?? 0)}/${Intl.NumberFormat("it-IT", {
                                        style: "decimal",
                                        useGrouping: true,
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 0,
                                    }).format(fase.quantita ?? 0)}pz`}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        noWrap
                                        sx={{flexGrow: 1}}
                                    >{`--> ${Intl.NumberFormat("it-IT", {
                                        style: "decimal",
                                        useGrouping: true,
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 2,
                                    }).format(fase.ore_previste_residue ?? 0)}/${Intl.NumberFormat("it-IT", {
                                        style: "decimal",
                                        useGrouping: true,
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 2,
                                    }).format(fase.ore_previste ?? 0)}h`}
                                    </Typography>

                                </Stack>
                            </Tooltip>
                            <Chip
                                label={fase.stato_display || "-"}
                                color={getStatoColor(fase.stato)}
                                size="small"
                                variant="outlined"
                                sx={{height: 18, fontSize: "0.65rem"}}
                            />
                        </Stack>

                        {/* Riga 2: reparto + avanzamento */}
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                                alignItems: "center",
                                mt: 0.25,
                            }}
                        >
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                noWrap
                                sx={{minWidth: 50}}
                            >
                                {fase.reparto_display || fase.risorsa_principale || "-"}
                            </Typography>

                            <Box sx={{minWidth: 110, maxWidth: 160}}>
                                <OperatoreSelectInline
                                    fase={fase}
                                    odpId={odpId}
                                    consentiModifica={consentiModificaOperatore}
                                    minWidth={0}
                                />
                            </Box>

                            <LinearProgress
                                variant="buffer"
                                value={(Number(fase.ore_lavorate || 0) / Number(fase.ore_previste || 1)) * 100}
                                valueBuffer={(Number(fase.ore_schedulate || 0) / Number(fase.ore_previste || 1)) * 100}

                                sx={{
                                    flexGrow: 1,
                                    height: 4,
                                    borderRadius: 2,
                                }}
                            />
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{minWidth: 28, textAlign: "right"}}
                            >
                                {Math.round((Number(fase.ore_lavorate || 0) / Number(fase.ore_previste || 1)) * 100)}%
                            </Typography>
                        </Stack>
                    </Box>
                </Box>
            ))}
        </Box>
    );
};

export default OdPSidebarFasi;